
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { mentorSchema, MentorFormValues } from "@/types/mentor";

export const useMentorForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const form = useForm<MentorFormValues>({
    resolver: zodResolver(mentorSchema),
    defaultValues: {
      full_name: "",
      title: "",
      company: "",
      experience_years: "",
      bio: "",
      expertise: "",
      availability: "",
      beta_tester: false,
      calendly_url: "",
    },
  });
  
  const nextStep = () => {
    if (step === 1) {
      const { full_name, title, company, experience_years } = form.getValues();
      if (!full_name || !title || !company || !experience_years) {
        toast({
          title: "Missing information",
          description: "Please complete all required fields before continuing",
          variant: "destructive",
        });
        return;
      }
    }
    
    if (step === 2) {
      const { bio, expertise, availability } = form.getValues();
      if (!bio || !expertise || !availability) {
        toast({
          title: "Missing information",
          description: "Please complete all required fields before continuing",
          variant: "destructive",
        });
        return;
      }
    }
    
    setStep((prev) => Math.min(prev + 1, 3));
  };
  
  const prevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };
  
  const onSubmit = async (data: MentorFormValues) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit your mentor application",
        variant: "destructive",
      });
      navigate("/auth");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Insert mentor data
      const { error } = await supabase.from("mentors").insert({
        user_id: user.id,
        position: data.title,
        company: data.company,
        experience_years: parseInt(data.experience_years),
        expertise: data.expertise.split(',').map(item => item.trim()),
        bio: data.bio,
        calendly_url: data.calendly_url || null,
      });
      
      if (error) throw error;
      
      // Update profile name if it's not already set
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();
        
      if (!profile?.full_name) {
        await supabase
          .from('profiles')
          .update({ full_name: data.full_name })
          .eq('id', user.id);
      }
      
      toast({
        title: "Application submitted",
        description:
          "Thank you for applying to become a mentor. We'll review your application and get back to you.",
      });
      
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error submitting mentor application:", error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return {
    form,
    step,
    nextStep,
    prevStep,
    isSubmitting,
    onSubmit,
  };
};
